import React, { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import './HousingServices.css';

// The 4 Core Services requested by user:
// 1. Security Services
// 2. Housekeeping Services
// 3. Manpower Outsourcing
// 4. Intelligence & Investigation
const coreServicesData = [
  {
    id: 'security-services',
    number: '01',
    title: 'Security Services',
    tagline: 'Armed & Unarmed Protection',
    description: 'Round-the-clock physical security guarding, automated access management, rapid response, and perimeter defense for commercial, residential, and industrial sites.',
    icon: '🛡️',
    gradient: 'linear-gradient(135deg, #1976D2 0%, #00B8D9 100%)',
    shadowColor: 'rgba(25, 118, 210, 0.28)',
    accentColor: '#1976D2',
    lightBg: '#EAF7FB',
    metrics: '24/7 Active Guarding',
    features: ['Armed & Unarmed Guards', 'Access & Perimeter Control', 'Emergency Quick Response'],
  },
  {
    id: 'housekeeping-services',
    number: '02',
    title: 'Housekeeping Services',
    tagline: 'Corporate & Society Hygiene',
    description: 'Immaculate deep cleaning, facility sanitation, mechanized floor care, and dedicated maintenance staff to maintain five-star environment standards.',
    icon: '✨',
    gradient: 'linear-gradient(135deg, #00B8D9 0%, #22C55E 100%)',
    shadowColor: 'rgba(0, 184, 217, 0.28)',
    accentColor: '#00B8D9',
    lightBg: '#E0F7FA',
    metrics: 'Eco-Friendly Sanitation',
    features: ['Corporate & Society Cleaning', 'Mechanized Deep Scrubbing', 'Waste & Hygiene Management'],
  },
  {
    id: 'manpower-outsourcing',
    number: '03',
    title: 'Manpower Outsourcing',
    tagline: 'Verified Skilled & Semi-Skilled Workforce',
    description: 'End-to-end recruitment, strict background vetting, payroll management, and reliable staffing across administration, logistics, and technical roles.',
    icon: '👥',
    gradient: 'linear-gradient(135deg, #F59E0B 0%, #F97316 100%)',
    shadowColor: 'rgba(249, 115, 22, 0.28)',
    accentColor: '#F97316',
    lightBg: '#FFF7ED',
    metrics: '100% Verified Staff',
    features: ['Background & Police Verified', 'Statutory PF/ESIC Compliant', 'Flexible On-Demand Deployment'],
  },
  {
    id: 'it-digitization',
    number: '04',
    title: 'IT & Digitization',
    tagline: 'ICT Labs, Data Entry & IT Operators',
    description: 'High-speed document scanning, digital archiving, complete ICT computer lab setup, certified data entry operators, and dependable office IT support infrastructure.',
    icon: '💻',
    gradient: 'linear-gradient(135deg, #0284C7 0%, #6366F1 100%)',
    shadowColor: 'rgba(2, 132, 199, 0.28)',
    accentColor: '#0284C7',
    lightBg: '#E0F2FE',
    metrics: 'Modern Tech & Digitization',
    features: ['ICT Computer Lab Setup & Maintenance', 'Skilled Data Entry & Computer Operators', 'High-Speed Document Digitization & Archiving'],
  },
];

const HousingServices = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-60px' });
  const [activeService, setActiveService] = useState(0);

  const current = coreServicesData[activeService];

  return (
    <section id="housing-services" className="housing-services" ref={sectionRef}>
      <div className="housing-services-bg" />

      <div className="housing-services-container">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 25 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="section-header"
        >
          <span className="section-badge badge-default">
            Core Service Architecture
          </span>
          <h2 className="section-title">
            AVISS <span className="text-[#1976D2] italic font-serif font-semibold">Core Solutions</span>
          </h2>
          <div className="section-header-line" />
          <p className="section-description">
            Four specialized operational wings delivering enterprise protection, verified workforce, and confidential vigilance.
          </p>
        </motion.div>

        {/* ===== 3D MODERN INTERACTIVE WORKSPACE ===== */}
        <div className="modern-3d-showcase">
          
          {/* TOP / SELECTOR: 4 3D Floating Interactive Tiles */}
          <div className="showcase-tabs-grid">
            {coreServicesData.map((item, index) => {
              const isActive = activeService === index;
              return (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 25 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ y: -6, scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setActiveService(index)}
                  className={`showcase-tab-card ${isActive ? 'is-active' : ''}`}
                  style={{
                    '--card-accent': item.accentColor,
                    '--card-shadow': item.shadowColor,
                  }}
                >
                  {/* 3D Glass Surface Glow */}
                  <div className="tab-card-glass" />
                  
                  <div className="tab-card-header">
                    <span className="tab-card-num">{item.number}</span>
                    <div 
                      className="tab-card-icon-bubble"
                      style={{ background: item.lightBg, color: item.accentColor }}
                    >
                      {item.icon}
                    </div>
                  </div>

                  <h3 className="tab-card-title">{item.title}</h3>
                  <p className="tab-card-tagline">{item.tagline}</p>

                  <div className="tab-card-indicator" style={{ background: item.gradient }} />
                </motion.div>
              );
            })}
          </div>

          {/* BOTTOM / 3D HERO STAGE: Dynamic Spotlight Showcase for Active Service */}
          <motion.div
            key={current.id}
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="showcase-stage-card"
          >
            {/* Background 3D Ambient Mesh */}
            <div 
              className="stage-glow-ambient"
              style={{ background: `radial-gradient(circle at 80% 30%, ${current.accentColor}25 0%, transparent 65%)` }}
            />

            <div className="stage-content-grid">
              
              {/* Left Info Column */}
              <div className="stage-text-col">
                <div className="stage-badge-row">
                  <span 
                    className="stage-num-badge"
                    style={{ background: current.gradient }}
                  >
                    WING {current.number}
                  </span>
                  <span className="stage-metric-tag" style={{ color: current.accentColor, background: current.lightBg }}>
                    ● {current.metrics}
                  </span>
                </div>

                <h3 className="stage-title">{current.title}</h3>
                <p className="stage-description">{current.description}</p>

                {/* 3 Key Capabilities in modern chip pills */}
                <div className="stage-features-list">
                  {current.features.map((feat, idx) => (
                    <div key={idx} className="stage-feature-pill">
                      <span className="stage-check-dot" style={{ background: current.accentColor }}>✓</span>
                      <span>{feat}</span>
                    </div>
                  ))}
                </div>

                <div className="stage-actions-row">
                  <a
                    href="#contact"
                    className="stage-primary-cta"
                    style={{
                      background: current.gradient,
                      boxShadow: `0 8px 24px ${current.shadowColor}`,
                    }}
                  >
                    Request Consultation →
                  </a>
                  <a href="#services" className="stage-secondary-link">
                    Explore Details
                  </a>
                </div>
              </div>

              {/* Right 3D Visual Pillar Display */}
              <div className="stage-visual-col">
                <div 
                  className="stage-3d-visual-card"
                  style={{
                    background: `linear-gradient(145deg, #FFFFFF 0%, ${current.lightBg} 100%)`,
                    border: `1.5px solid ${current.accentColor}35`,
                    boxShadow: `0 20px 50px ${current.shadowColor}`,
                  }}
                >
                  <div className="stage-3d-emblem-wrap">
                    <div 
                      className="stage-3d-emblem-bg"
                      style={{ background: current.gradient }}
                    />
                    <span className="stage-3d-big-icon">{current.icon}</span>
                  </div>

                  <div className="stage-3d-meta">
                    <span className="stage-3d-tag">AVISS CERTIFIED WING</span>
                    <h4 className="stage-3d-headline">{current.title}</h4>
                    <span className="stage-3d-sub">{current.tagline}</span>
                  </div>
                </div>
              </div>

            </div>
          </motion.div>

        </div>

      </div>
    </section>
  );
};

export default HousingServices;